import type { LogContext, LogEvent } from "./types";

/**
 * PII/secrets redaction на межі dispatcher-а (ADR-0011 D5). Застосовується
 * **перед** fan-out — отже перед будь-яким sink-ом, включно з майбутнім remote.
 * За цю межу не виходить жоден токен/cookie/пароль/CSRF/чутливе тіло.
 *
 * Правила:
 * 1. `context.error`:
 *    - AppError-подібний → безпечна проекція `{ kind, status, code }` (raw axios-error
 *      із `config.headers`/`config.data`/cookie — НЕ потрапляє в `LogEvent`);
 *    - `Error` → `{ name, message, stack }` (stack лишається для DevTools, секретів не несе);
 *    - інше → рекурсивна санітизація.
 * 2. key-denylist (case-insensitive, рекурсивно) → `[redacted]`.
 * 3. `url`/`path`-ключі → лише pathname (query відкидається).
 * 4. safe-stringify: обрив циклів + обмеження глибини (axios-обʼєкти циклічні).
 */
const DENY_KEY = /(password|token|secret|authorization|cookie|csrf|set-cookie)/i;
const URL_KEYS = new Set(["url", "path"]);
const MAX_DEPTH = 4;

function pathOnly(value: unknown): unknown {
  if (typeof value !== "string") {
    return value;
  }
  try {
    return new URL(value, "http://_").pathname;
  } catch {
    return value;
  }
}

function isAppErrorLike(
  e: unknown,
): e is { kind: unknown; status?: unknown; code?: unknown } {
  return (
    typeof e === "object"
    && e !== null
    && "kind" in e
    && "retriable" in e
  );
}

function projectError(e: unknown): unknown {
  if (isAppErrorLike(e)) {
    return { kind: e.kind, status: e.status, code: e.code };
  }
  if (e instanceof Error) {
    return { name: e.name, message: e.message, stack: e.stack };
  }
  return sanitizeValue(e, 1, new WeakSet());
}

function sanitizeValue(
  value: unknown,
  depth: number,
  seen: WeakSet<object>,
): unknown {
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (seen.has(value)) {
    return "[circular]";
  }
  if (depth >= MAX_DEPTH) {
    return "[truncated]";
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((v) => sanitizeValue(v, depth + 1, seen));
  }

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    if (DENY_KEY.test(k)) {
      out[k] = "[redacted]";
    } else if (URL_KEYS.has(k.toLowerCase())) {
      out[k] = pathOnly(v);
    } else {
      out[k] = sanitizeValue(v, depth + 1, seen);
    }
  }
  return out;
}

export function sanitize(event: LogEvent): LogEvent {
  const seen = new WeakSet<object>();
  const ctx = event.context ?? {};
  const out: LogContext = {};

  for (const [k, v] of Object.entries(ctx)) {
    if (k === "error") {
      out.error = projectError(v);
    } else if (DENY_KEY.test(k)) {
      out[k] = "[redacted]";
    } else if (URL_KEYS.has(k.toLowerCase())) {
      out[k] = pathOnly(v);
    } else {
      out[k] = sanitizeValue(v, 1, seen);
    }
  }

  return { ...event, context: out };
}
